import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import slugify from 'slugify';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

// Components
import { CatalogHead } from 'src/pages/CatalogPage/components/CatalogHead/CatalogHead';
import { AuctionItems } from 'src/pages/CatalogPage/components/AuctionItems/AuctionItems';
import { PaginationBar } from 'src/pages/CatalogPage/components/PaginationBar/PaginationBar';
import { HandleNoResults } from 'src/pages/CatalogPage/components/HandleNoResults/HandleNoResults';
import { Banner, Button, Grid2, Spinner, Typography } from '@riac/design-system';

import {
  TAuctionCollections,
  IAuctionData,
  IAuctionMappings,
  IMapping,
  TAuctionStatusID,
  TAuctionTypeID,
} from 'src/pages/CatalogPage/Catalog.i';
import {
  IFacets,
  ISearchInput,
  TDisplayedAuctionItems,
  TLessWordSearches,
  TSavedSearchName,
  TSpellcheck,
  TTotalResults,
  ICatalogSearch,
  SearchPostData,
} from 'src/pages/CatalogPage/CatalogPage.i';
import { usePrefetchSuspense } from 'src/lib/usePrefetch';
import { useFetch } from 'src/lib/FetchContext';
import { CatalogHistoryContext } from 'src/lib/CatalogHistoryContext';
import { SsrInitDataContext } from 'src/lib/SsrInitDataContext';
import { useWindowSize } from 'src/lib/useWindowSize';
import { screenWidths } from '@riac/design-system';
import { WishlistActionContext } from 'src/lib/WishlistActionContext';
import { LoginContext } from 'src/lib/LoginContext';
import { Page404 } from 'src/pages/Page404';
import { BiddingContextProvider } from 'src/lib/BiddingContext';
import { SocketContext } from 'src/lib/SocketContext';
import { CatalogHeadSearchBar } from './components/CatalogHead/SearchBar/SearchBar';
import { FilterMenu } from './components/FilterMenu/FilterMenu';

interface ICatalogPageProps {
  auctionId: number;
  auctionData: IAuctionData;
  mappings: IAuctionMappings;
  collections: TAuctionCollections;
}

export interface ICatalogPageState {
  displayedAuctionItems: TDisplayedAuctionItems;
  facets: IFacets;
  lessWordSearches: TLessWordSearches;
  savedSearchName: TSavedSearchName;
  spellcheck: TSpellcheck;
  totalResults: TTotalResults;
}

interface ICatalogContext {
  auctionLive: boolean;
  auctionId: number;
  auctionStatusId: TAuctionStatusID;
  auctionTypeId: TAuctionTypeID;
  auctionEndDate: dayjs.Dayjs;
  auctionStartDate: dayjs.Dayjs;
  auctionEndTime: string;
  auctionCloseRate: number;
  lotExtensionTime: number;
  auctionTitle: string;
  auctionSubtitle?: string;
  auctionLocCity: string;
  auctionLocState: string;
  searchResult: any;
  refreshTimedLots: () => Promise<void>;
  setAuctionSearch: (searchResult: any) => void;
  compactMode: TCompactMode;
  gotoAddress(address: string): void;
  setCompactMode(value: TCompactMode): void;
  activeLot: number;
}

type TCompactMode = 'true' | 'false';

export const CatalogContext = React.createContext<Partial<ICatalogContext>>({});

interface ISearchInputLocation {
  search: string;
  pathname: string;
}

const timers = {};

export const CatalogPage = ({
  auctionId,
  auctionData,
  mappings,
  collections,
}: ICatalogPageProps): JSX.Element => {
  const reactRouterLocation = useLocation();
  const [activeLocation, setActiveLocation] = useState<ISearchInputLocation>(reactRouterLocation);
  const [searchBusy, setSearchBusy] = useState(false);
  const { setLatestCatalogPage, scrollToTop, scrollToTopRef } = useContext(CatalogHistoryContext);
  const { fetch } = useFetch();
  const { serverCompactMode } = useContext(SsrInitDataContext);
  const { width } = useWindowSize();
  const [forceCompactMode, setForceCompactMode] = useState(false);
  const { isLoggedIn } = useContext(LoginContext);
  const wishlistActions = useContext(WishlistActionContext);
  const [solrError, setSolrError] = useState<boolean>(false);
  const { socket, setOnOnTimePage } = useContext(SocketContext);
  const firstLoad = useRef<boolean>(true);


  useEffect(() => {
    if(isLoggedIn) {
      wishlistActions.setAuction(auctionId);
    }
  }, [auctionId]);

  const getSearchPostData = (searchInput: ISearchInput): SearchPostData => ({
    auctionid: auctionId,
    keywords: decodeURIComponent(searchInput.keywords),
    start: Math.max(0, (searchInput.pageNumber - 1) * searchInput.perPage),
    perpage: searchInput.perPage,
    sortfield: searchInput.sortBy,
    hlo: decodeURIComponent(searchInput.hlo),
    amcode: decodeURIComponent(searchInput.amcode),
    manu: decodeURIComponent(searchInput.manu),
    caliber: decodeURIComponent(searchInput.caliber),
    subcaliber: decodeURIComponent(searchInput.subcaliber),
    savedsearch: searchInput.savedSearch,
    completedlots: searchInput.complete ? 1 : 0,
    day: decodeURIComponent(searchInput.session),
    gotoLot: searchInput.lotNumber,
    onlyLotsBidOn: searchInput.onlyLotsBidOn,
    timedAuction: auctionData?.AuctionTypeID === TIMED_AUCTION_TYPE_ID,
    hasBids: searchInput.hasBids,
  });

  useEffect(() => {
    if(width && width < screenWidths.desktop) {
      setForceCompactMode(true);
    } else {
      setForceCompactMode(false);
    }
  }, [width]);

  // Serialize search input for REST search call from location info
  const collectSearchInputByLocation = (location: ISearchInputLocation): ISearchInput => {
    const params = location.pathname.split('/').slice(3).map((x) => decodeURIComponent(x)).filter((x) => x.length > 0);
    let searchInput: ISearchInput = {
      pageNumber: 1,
      complete: false,
      perPage: 50,
      keywords: '',
      lotNumber: '',
      savedSearch: '',
      sortBy: 'LotNumber,ASC',
      amcode: '',
      hlo: '',
      manu: '',
      caliber: '',
      subcaliber: '',
      session: '',
      hasBids: '',
      onlyLotsBidOn: false,
    };

    const queryString = qs.parse(location.search.substring(1));
    if(queryString.lotNumber) {
      searchInput.lotNumber = queryString.lotNumber?.toString();
    } else {
      searchInput = {
        pageNumber: queryString.pageNumber ? parseInt(queryString.pageNumber?.toString()) : 1,
        complete: queryString.complete === 'true',
        onlyLotsBidOn: queryString.onlyLotsBidOn === 'true',
        perPage: queryString.perPage
          ? Math.max(1, Math.min(500, parseInt(queryString.perPage?.toString())))
          : 50,
        keywords: queryString.keywords ? decodeURIComponent(queryString.keywords?.toString()) : '',
        lotNumber: '',
        savedSearch: queryString.savedSearch
          ? decodeURIComponent(queryString.savedSearch?.toString())
          : '',
        sortBy: queryString.sortBy?.toString() || 'LotNumber,ASC',
        amcode: '',
        hlo: '',
        manu: '',
        caliber: '',
        subcaliber: '',
        session: '',
        hasBids: queryString.hasBids ? 'false' : '',
      };
      // match params to a mapping and assign value to the corresponding field in search data
      let currentParams = params;
      if(mappings) {
        Object.keys(mappings)?.forEach((field) => {
          if(currentParams.length > 0) {
            const mappingParam = mappings[field].find((mapping: IMapping) =>
              currentParams.includes(mapping.slug)
            );
            if(mappingParam) {
              searchInput[field] = mappingParam.value;
              currentParams = currentParams.filter((p) => p !== mappingParam.slug);
            }
          }
        });
      }

      // if any params are left over not matching mappings, treat them as search keywords
      if(currentParams.length > 0) {
        searchInput.keywords += ' ' + currentParams.join(' ');
      }

      if(searchInput.savedSearch && isNaN(parseInt(searchInput.savedSearch?.toString()))) {
        // collection search by name. convert to ID if exists
        const collectionObj = collections.filter(
          (collection) => slugify(collection.Collection) === searchInput.savedSearch
        )[0];
        if(collectionObj) {
          searchInput.savedSearch = collectionObj.CID;
        }
      }
    }
    return searchInput;
  };

  const performSearch = async (searchInput: ISearchInput, signal?: AbortSignal):Promise<any> => {
    setSolrError(false);
    try {
      const searchReq = await fetch('/api/catalog/search', {
        method: 'PUT',
        signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(getSearchPostData(searchInput)),
      });
      const solrResults = await searchReq.json();
      if(!solrResults || !solrResults.success) {
        setSolrError(true);
      }
      return {
        displayedAuctionItems: solrResults.solrRes?.Items || [],
        facets: solrResults.solrRes?.Facets || {
          guage: {},
          gunType: {},
          manufacturerName: {},
          amCodeLot: {},
          hlo: {},
          guageNum: {},
          guageCategory: {},
          lotNumber: {},
        },
        lessWordSearches: solrResults.solrRes?.LessWordSearches || [],
        savedSearchName: solrResults.solrRes?.SearchName || '',
        spellcheck: solrResults.solrRes?.Spellcheck || [],
        totalResults: solrResults.solrRes?.Count || 0,
        modified: Date.now(),
      };
    } catch (e) {
      if(!(e instanceof DOMException)) {
        setSolrError(true);
        console.error(e);
      }
      return undefined;
    }
  };

  const searchInput = collectSearchInputByLocation(activeLocation);

  // Initial search for server side rendering, only fires once.
  const prefetchAuctionSearch = usePrefetchSuspense('initial-catalog-search', async () => {
    return await performSearch(searchInput);
  }).read();

  const [auctionSearch, setAuctionSearch] = useState(prefetchAuctionSearch);

  const [compactMode, setCompactMode] = useState<TCompactMode>(
    (serverCompactMode ? serverCompactMode : Cookies.get('compact_mode') || 'false') as TCompactMode
  );

  useEffect(() => {
    Cookies.set('compact_mode', compactMode);
  }, [compactMode]);

  const [, setCancelPreviousSearch] = useState<() => void | undefined>();

  useEffect(() => {
    // make sure we don't overwrite returnLot
    window.history.replaceState(Object.assign({}, window.history.state,
      {
        location: {
          pathname: window.location.pathname,
          search: window.location.search,
        },
        catalogAuctionID: auctionId,
      }),
    document.title,
    );
    setLatestCatalogPage(`${window.location.pathname}${window.location.search && `${window.location.search}`}`);
    const popstate = async (event:PopStateEvent):Promise<void> => {
      if(event?.state?.location && event?.state?.catalogAuctionID) {
        setCancelPreviousSearch((old) => {
          old && old();
          return null;
        });
        const newLocation = event.state.location;
        const newSearchInput = collectSearchInputByLocation(newLocation);
        setActiveLocation(newLocation);
        let cancelled = false;
        let abortController: AbortController | undefined;
        try {
          abortController = new AbortController();
        } catch (e) {
          console.error('Abort controller not supported');
        }
        setCancelPreviousSearch(() => () => {
          cancelled = true;
          abortController?.abort();
        });
        setSearchBusy(true);
        try {
          const searchResults = await performSearch(newSearchInput, abortController?.signal);
          if(!cancelled) {
            setAuctionSearch(searchResults);
            setCancelPreviousSearch(null);
          }
        } finally {
          if(!cancelled) {
            setSearchBusy(false);
          }
        }
      }
    };
    window.addEventListener('popstate', popstate);

    return () => {
      setCancelPreviousSearch((old) => {
        old && old();
        return null;
      });
      window.removeEventListener('popstate', popstate);
      for(const key in timers) {
        if(Object.hasOwnProperty.call(timers, key)) {
          clearTimeout(timers[key]);
          delete timers[key];
        }
      }
    };
  }, []);

  useEffect(() => {
    if(socket && auctionData?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      socket.emit('join-room', `timed-auction-${auctionId}`);
      setOnOnTimePage(true);
    } else {
      setOnOnTimePage(false);
    }
    return () => {
      if(auctionData?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        socket.emit('leave-room', `timed-auction-${auctionId}`);
        setOnOnTimePage(false);
      }
    };
  }, [socket?.id]);

  const searchResult: ICatalogPageState = {
    displayedAuctionItems: [],
    facets: {
      guage: {},
      gunType: {},
      manufacturerName: {},
      amCodeLot: {},
      hlo: {},
      guageNum: {},
      guageCategory: {},
      lotNumber: {},
      hasBids: {},
    },
    lessWordSearches: [],
    savedSearchName: '',
    spellcheck: [],
    totalResults: 0,
  };

  const setTimedData = (displayedItem, currentBid, endDate, highBidder, highBidAmount) => {
    const newItem = { ...displayedItem };
    newItem.EndDate = endDate;
    newItem.CurrentBid = currentBid;
    // If we are the high bidder and the current bid is over our high bid, we are no longer the high bid
    if(newItem.HighBidder && currentBid > newItem.HighBidAmount) {
      newItem.HighBidder = false;
      newItem.PreviousBid = 1;
      newItem.HighBidAmount = null;
    }
    // if we are now the high bidder
    if(typeof highBidder !== 'undefined' && typeof highBidAmount !== 'undefined') {
      newItem.HighBidAmount = highBidAmount;
      newItem.HighBidder = highBidder;
      if(!highBidder) {
        newItem.PreviousBid = 1;
        newItem.HighBidAmount = null;
      }
    }
    return newItem;
  };

  const addTimer = (aId, lotNumber, endDate) => {
    const key = `${aId}-${lotNumber}`;
    if(timers[key]) {
      clearTimeout(timers[key]);
    }
    const now = dayjs();
    const end = dayjs.tz(endDate, 'America/Chicago'); // Interpret end date as America/Chicago
    const diff = end.diff(now);

    if(diff > 0 && diff < 86400000) {
      timers[key] = setTimeout(async () => {
        await updateTimedInfo(aId, [lotNumber]);
      }, diff);
    }
  };

  const clearTimer = (aId, lotNumber, endDate) => {
    const key = `${aId}-${lotNumber}`;
    if(timers[key]) {
      clearTimeout(timers[key]);
    }
  };

  const updateTimedInfo = async (aId:number, lotNumbers:number[]) => {
    const timedInfo = await(await fetch('/api/timed-auction/refresh-lot-data', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        auctionId: aId,
        lotNumbers: lotNumbers.join(','),
      }),
    })).json();
    setAuctionSearch((oldSearch:ICatalogPageState) => ({
      ...oldSearch,
      displayedAuctionItems: oldSearch.displayedAuctionItems.map((displayedItem) => {
        if(typeof timedInfo.data[displayedItem.LotNumber] === 'undefined') {
          return displayedItem;
        }
        addTimer(aId, displayedItem.LotNumber, timedInfo.data[displayedItem.LotNumber].endDate);
        return setTimedData(
          displayedItem,
          timedInfo.data[displayedItem.LotNumber].currentBid,
          timedInfo.data[displayedItem.LotNumber].endDate,
          timedInfo.data[displayedItem.LotNumber].highBidder,
          timedInfo.data[displayedItem.LotNumber].highBidAmount
        );
      }),
    }));
  };

  const auctionItemIds = auctionSearch?.displayedAuctionItems?.map((item)=>item.AuctionItemID);

  const triggerTimedUpate = useCallback(async ()=>{
    const lotNumbers = auctionSearch?.displayedAuctionItems?.map((item) => item.LotNumber);
    await updateTimedInfo(auctionId, lotNumbers);
  }, [auctionSearch?.modified]);

  useEffect(() => {
    const timers = [];
    if(auctionData?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      auctionSearch?.displayedAuctionItems?.forEach((item) => {
        addTimer(auctionId, item.LotNumber, item.EndDate);
      });
    }
    const handleLotUpdate = (
      auctionItemID: number | string,
      currentBid: number,
      endDate: string,
      highBidder?: boolean,
      highBidAmount?: number | null,
    ): void => {
      const needsToUpdate = auctionSearch?.displayedAuctionItems?.some((ai) => String(ai.AuctionItemID) === String(auctionItemID));
      if(!needsToUpdate) {
        return;
      }
      setAuctionSearch((oldSearch:ICatalogPageState) => ({
        ...oldSearch,
        displayedAuctionItems: oldSearch.displayedAuctionItems.map((displayedItem) => {
          if(String(displayedItem.AuctionItemID) !== String(auctionItemID)) {
            return displayedItem;
          }
          addTimer(auctionId, displayedItem.LotNumber, endDate);
          return setTimedData(displayedItem, currentBid, endDate, highBidder, highBidAmount);
        }),
      }));
    };
    const visChangeHandler = async ()=> {
      if(!document.hidden) {
        await triggerTimedUpate();
      }
    };
    const reconHandler = async () => {
      await triggerTimedUpate();
    };
    if(auctionData?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
      window.addEventListener('focus', visChangeHandler);
      if(socket) {
        socket.on('lot update', handleLotUpdate);
        socket.on('reconnect', reconHandler);
        socket.on('connect', reconHandler);
      }
    }
    return () => {
      if(auctionData?.AuctionTypeID === TIMED_AUCTION_TYPE_ID) {
        auctionSearch?.displayedAuctionItems?.forEach((item) => {
          clearTimer(auctionId, item.LotNumber, item.EndDate);
        });
        window.removeEventListener('focus', visChangeHandler);
        if(socket) {
          socket.off('lot update', handleLotUpdate);
          socket.off('reconnect', reconHandler);
          socket.off('connect', reconHandler);
        }
      }
    };
  }, [auctionSearch?.modified]);

  if(auctionData === null || !mappings) {
    // prefetch data not returned yet
    return null;
  }

  if(!auctionData || !auctionData.AuctionID) {
    // invalid auction ID or other error
    return <Page404/>;
  }

  const gotoAddress = async (address:string):Promise<void> => {
    setCancelPreviousSearch((old) => {
      old && old();
      return null;
    });
    const hasQS = address.indexOf('?') > -1;
    const newLocation = {
      search: hasQS && address.substring(address.indexOf('?')) || '',
      pathname: hasQS && decodeURIComponent(address.substring(address.indexOf('/'), address.indexOf('?'))) || address,
    };
    const newSearchInput = collectSearchInputByLocation(newLocation);
    setActiveLocation(newLocation);
    setLatestCatalogPage(address);
    let cancelled = false;
    let abortController: AbortController | undefined;
    try {
      abortController = new AbortController();
    } catch (e) {
      console.error('Abort controller not supported');
    }
    setCancelPreviousSearch(() => () => {
      cancelled = true;
      abortController?.abort();
    });
    setSearchBusy(true);
    try {
      // make sure we don't overwrite returnLot
      window.history.pushState(Object.assign(
        {},
        window.history.state,
        {
          location: newLocation,
          catalogAuctionID: auctionId,
        }),
      window.document.title,
      address
      );
    } catch (e) {
      console.error('error pushing window state', e);
    }
    try {
      // reset auction search so previous spellchecks go away during new search
      // we can't just set auctionSearch to undefined here because that short circuits the prefetch detection logic
      // and instead of resetting Spellcheck and LessWordSearches it just loads them from prefetchAuctionData
      // which leaves the old Spellcheck and LessWordSearches loaded from the initial prefetch search displayed
      let spellcheckResetAuctionSearch:ICatalogPageState;

      // If this isn't the first run
      if(auctionSearch) {
        spellcheckResetAuctionSearch = Object.assign({}, auctionSearch, { Spellcheck: [], LessWordSearches: [] });
      }
      // else this must be the first run and auctionSearch is undefined
      else if(prefetchAuctionSearch) {
        spellcheckResetAuctionSearch = Object.assign({}, prefetchAuctionSearch, {
          Spellcheck: [],
          LessWordSearches: [],
        });
      }

      setAuctionSearch(spellcheckResetAuctionSearch);
      const searchResults = await performSearch(newSearchInput, abortController?.signal);
      if(!cancelled) {
        setAuctionSearch(searchResults);
        setCancelPreviousSearch(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if(!cancelled) {
        setSearchBusy(false);
        scrollToTop();
      }
    }
  };

  useEffect(() => {
    if(firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    gotoAddress(reactRouterLocation.pathname).then();
  }, [reactRouterLocation.pathname]);

  // Build url query string from catalog paging options and push to trigger search
  const updateCatalogSearch: ICatalogSearch = function(changes) {
    if(changes.lotNumber) {
      gotoAddress(`/catalog/${auctionId}/?lotNumber=${changes.lotNumber}`).then();
      return;
    }
    const searchOptions = qs.parse(location.search.substring(1));
    Object.assign(searchOptions, changes);
    // remove any options that have been set back to default
    Object.keys(searchOptions).forEach((key) => {
      if(
        !searchOptions[key]
        || (key === 'pageNumber' && parseInt(searchOptions.pageNumber?.toString()) === 1)
        || (key === 'complete' && searchOptions.complete?.toString() === 'false')
        || (key === 'perPage' && parseInt(searchOptions.perPage?.toString()) === 50)
        || (key === 'sortBy' && searchOptions[key] === 'LotNumber,ASC')
      ) {
        delete searchOptions[key];
      }
    });
    gotoAddress(
      `${location.pathname}${Object.keys(searchOptions).length > 0 ? `?${qs.stringify(searchOptions)}` : ''}`
    ).then();
  };

  // Build new url with filter option params and push to trigger search
  function updateCatalogFilter(slug: string, field: keyof IAuctionMappings):void {
    const searchOptions = qs.parse(location.search.substring(1));
    if(Object.prototype.hasOwnProperty.call(searchOptions, 'pageNumber')) {
      delete searchOptions.pageNumber;
    }

    let newParams = location.pathname.split('/')
      .slice(3)
      .map((x) => decodeURIComponent(x)).filter((x) => x.length > 0);
    if(newParams.length === 0) {
      newParams = [slug];
    } else {
      // find if field already has an existing slug/param
      const oldFieldParam = mappings[field].find((mapp: IMapping) => newParams.includes(mapp.slug));
      if(oldFieldParam) {
        const i = newParams.indexOf(oldFieldParam.slug);
        // remove old param and replace it with new slug or nothing if 'ALL' was selected
        newParams.splice(i, 1, slug || null);
      } else {
        newParams.push(slug);
      }
    }
    // Remove subcaliber slug from params if caliber filter is reset with empty slug
    if(field === 'caliber' && !slug) {
      mappings.subcaliber.forEach((mapping) => {
        if(newParams.includes(mapping.slug)) {
          const i = newParams.indexOf(mapping.slug);
          newParams.splice(i, 1);
        }
      });
    }
    const newPath = `/catalog/${auctionId}/${newParams.join('/')}/`
      + `${Object.keys(searchOptions).length > 0 ? `?${qs.stringify(searchOptions)}` : ''}`
        .replace(
          /\/\//g,
          '/'
        );
    gotoAddress(newPath).then();
  }

  let auctionLive = false;
  const auctionEndDate = dayjs.tz(auctionData.EndDate, 'America/Chicago');
  const auctionStartDate = dayjs.tz(auctionData.StartDate, 'America/Chicago');
  const now = dayjs().tz('America/Chicago');
  if(auctionStartDate.isBefore(now) && auctionEndDate.add(24, 'hour').isAfter(now)) {
    auctionLive = true;
  }


  const limitstart = (searchInput.pageNumber - 1) * searchInput.perPage + 1;
  let limittop = limitstart + searchInput.perPage - 1;
  if(auctionSearch?.totalResults < 50 || limittop > auctionSearch?.totalResults) {
    limittop = auctionSearch.totalResults;
  }

  // <ONE TIME HACK REPLACE ME>
  if(auctionData.AuctionID == 2094) {
    auctionData.Subtitle = 'Guns & Coffee';
  }
  // </ONE TIME HACK REPLACE ME>

  return (
    <CatalogContext.Provider
      value={{
        auctionLive,
        auctionId: auctionData.AuctionID,
        auctionStatusId: auctionData.AuctionStatusID,
        auctionTypeId: auctionData.AuctionTypeID,
        auctionLocCity: auctionData.LocCity,
        auctionLocState: auctionData.LocState,
        auctionEndDate,
        auctionStartDate,
        auctionEndTime: auctionData.EndTime,
        auctionCloseRate: auctionData.CloseRate,
        lotExtensionTime: auctionData.ExtensionTime,
        auctionTitle: auctionData.Title,
        auctionSubtitle: auctionData.Subtitle,
        searchResult: searchResult,
        setAuctionSearch,
        compactMode: forceCompactMode ? 'true' : compactMode,
        setCompactMode,
        gotoAddress,
        activeLot: auctionSearch?.solrRes?.activeLot,
      }}
    >
      <BiddingContextProvider
        auctionId={auctionId}
        auctionTypeId={auctionData.AuctionTypeID}
      >
        <CatalogHead
          excerpt={auctionData.Excerpt}
          startTime={auctionData.StartTime}
          startDate={auctionData.StartDate}
          auctionTypeID={auctionData.AuctionTypeID}
          keywords={searchInput.keywords}
          lotNumber={searchInput.lotNumber}
          updateSearch={updateCatalogSearch}
          complete={searchInput.complete}
          onlyLotsBidOn={searchInput.onlyLotsBidOn}
          searchPostData={getSearchPostData(searchInput)}
          sessions={mappings.session}
          locationAddress={auctionData.LocAddress}
          locationCity={auctionData.LocCity}
          locationState={auctionData.LocState}
          locationZip={auctionData.LocZip}
        />
        <Grid2>
          {/* <div className='flex-col gap-2 hidden xl:flex w-64 sm:max-w-64'>
            <Typography size='xl' weight='bold' color='text' className='tracking-tight'>Filters</Typography>
            <FilterMenu
              facets={auctionSearch.facets}
              updateFilter={updateCatalogFilter}
              mappings={mappings}
              searchInput={searchInput}
              collections={collections}
              searchBusy={searchBusy}
              searchName={auctionSearch.savedSearchName}
            />
          </div> */}
          {typeof auctionSearch === 'undefined' ? <Spinner />
            : <div className='flex flex-1 items-start'>
              <div ref={scrollToTopRef as any}/>
              <div className='flex flex-col gap-5 flex-1'>
                <PaginationBar
                  loc='top'
                  searchBusy={searchBusy}
                  pageNumber={searchInput.pageNumber}
                  sortBy={searchInput.sortBy}
                  perPage={searchInput.perPage}
                  updateSearch={updateCatalogSearch}
                  totalResults={auctionSearch?.totalResults}
                  auctionTypeId={auctionData.AuctionTypeID}
                  facets={auctionSearch?.facets}
                  updateFilter={updateCatalogFilter}
                  mappings={mappings}
                  searchInput={searchInput}
                  collections={collections}
                  searchName={auctionSearch?.savedSearchName}
                />
                <CatalogHeadSearchBar
                  key={`${searchInput.keywords} ${searchInput.lotNumber} ${searchInput.complete}`}
                  keywords={searchInput.keywords}
                  lotNumber={searchInput.lotNumber}
                  updateSearch={updateCatalogSearch}
                  complete={searchInput.complete}
                  onlyLotsBidOn={searchInput.onlyLotsBidOn}
                  totalResults={auctionSearch?.totalResults}
                  sortBy={searchInput.sortBy}
                  perPage={searchInput.perPage}
                  searchBusy={searchBusy}
                  pageNumber={searchInput.pageNumber}
                  auctionTypeId={auctionData.AuctionTypeID}
                />
                <HandleNoResults
                  searchInput={searchInput}
                  searchResult={auctionSearch}
                  updateSearch={updateCatalogSearch}
                  searchBusy={searchBusy || !prefetchAuctionSearch || solrError}
                />
                <div className='flex flex-col gap-3'>
                  <Typography color='text'>Showing <span className='font-bold'>{Math.min(limittop, limitstart)}-{limittop}</span> of <span className='font-bold'>
                    {new Intl.NumberFormat('en-US').format(auctionSearch?.totalResults)}
                  </span> results</Typography>
                  {(solrError == true)
                    ? (
                      <Banner color='error' children={<>Error: Something went wrong with the search. Please try again,
                      or <Button variant='text' className='p-0' to='/contact'>contact customer support</Button> if this error continues
                      to occur.</>}/>
                    )
                    : (searchBusy || !prefetchAuctionSearch) && <Spinner/>
                  || <AuctionItems
                    displayedAuctionItems={auctionSearch?.displayedAuctionItems}
                    showPricesRealized={auctionData.PricesRealized}
                  />
                  }
                </div>
                <PaginationBar
                  loc='bottom'
                  searchBusy={searchBusy}
                  pageNumber={searchInput.pageNumber}
                  sortBy={searchInput.sortBy}
                  perPage={searchInput.perPage}
                  updateSearch={updateCatalogSearch}
                  totalResults={auctionSearch?.totalResults}
                  auctionTypeId={auctionData.AuctionTypeID}
                  facets={auctionSearch?.facets}
                  updateFilter={updateCatalogFilter}
                  mappings={mappings}
                  searchInput={searchInput}
                  collections={collections}
                  searchName={auctionSearch?.savedSearchName}
                />
              </div>
            </div>}
        </Grid2>
      </BiddingContextProvider>
    </CatalogContext.Provider>
  );
};
